import React, { useEffect, useState } from 'react'
import {
  Button,
  CloseIcon,
  DialogContent,
  IconButton,
  TextField,
  Tooltip,
} from '@resiliatech/birdsong'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useAppState } from 'Context/AppContext/AppContext'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import { useMutation } from '@apollo/client'
import {
  HeaderContainer,
  TitleSection,
  ButtonContainer,
  Popover,
} from 'Components/Pages/Impact/GoalPage/ProgressUpdate/ProgressUpdateDialog/ProgressUpdateDialog.styled'
import { Attachment } from '../../../../../Generated/graphql-types'
import UPDATE_ATTACHMENT from '../../../../../Apollo/Mutations/updateAttachment'
import { GOALS } from '../../../../../Apollo/Queries/Goals'

type ProgressUpdateResult = {
  success: boolean
  error?: string
}

type ProgressUpdateDialogProps = {
  attachment: Attachment | undefined
  open: boolean
  onUpdateComplete?: (result: ProgressUpdateResult) => void
  onClose: () => void
  showToast?: boolean
  standalone?: boolean
}

export const AttachmentUpdateDialog = ({
  attachment,
  open,
  onUpdateComplete,
  onClose,
  showToast,
  standalone = false,
}: ProgressUpdateDialogProps) => {
  const [inputValue, setInputValue] = useState<string>(
    attachment?.filename || ''
  )

  useEffect(() => {
    setInputValue(attachment?.filename || '')
  }, [attachment])

  const [isDialogVisible, setIsDialogVisible] = useState(true)
  const { popToast } = useSnackbar()

  const { nonprofitOrgConfig } = useAppState()
  const { orgId: nonprofitOrgId } = nonprofitOrgConfig

  const hasValidInput = inputValue.trim().length > 0

  const handleClose = () => {
    setIsDialogVisible(true)
    setInputValue(attachment?.filename || '')
    onClose()
  }

  const handleDoNotUpdate = () => {
    handleClose()
  }

  const [updateAttachment, { loading }] = useMutation(UPDATE_ATTACHMENT, {
    onCompleted: () => {
      if (onUpdateComplete) {
        onUpdateComplete({ success: true })
      }
      if (showToast) {
        popToast({
          content: `Your attachment has been successfully updated.`,
        })
      }
      if (standalone) {
        handleClose()
      }
    },
    onError: (error) => {
      if (onUpdateComplete) {
        onUpdateComplete({ success: false, error: error.message })
      }
      if (showToast) {
        popToast({
          content: `Something went wrong.`,
        })
      }
      if (standalone) {
        handleClose()
      }
    },
    refetchQueries: [
      {
        query: GOALS,
        variables: { activeOnly: true, nonprofitOrgId },
      },
    ],
  })

  const handleSubmit = async () => {
    if (!hasValidInput || !attachment) return

    const updateInfo = {
      id: attachment.id,
      filename: inputValue.trim(),
    }
    await updateAttachment({
      variables: updateInfo,
    })
    handleClose()

    popToast({
      content: `Your attachment has been successfully renamed.`,
    })
  }

  return (
    <Popover
      open={open && isDialogVisible}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      BackdropProps={{
        invisible: true,
        style: { backgroundColor: 'rgba(26, 26, 26, 0.5)' },
      }}
    >
      <div style={{ margin: '40px' }}>
        <HeaderContainer>
          <TitleSection>
            Update Attachment
            <Tooltip
              title={<>Update the filename for this attachment.</>}
              placement='right'
              style={{ cursor: 'pointer' }}
            >
              <InfoOutlinedIcon
                fontSize='small'
                color='action'
                style={{ margin: 0, padding: 0, display: 'flex' }}
              />
            </Tooltip>
          </TitleSection>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </HeaderContainer>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            label='Update the filename for this attachment.'
            margin='normal'
          />
          <ButtonContainer>
            <Button
              variant='text'
              color='error'
              size='large'
              onClick={handleDoNotUpdate}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              label='Edit Attachment'
              isDisabled={!hasValidInput || loading}
              onClick={handleSubmit}
            />
          </ButtonContainer>
        </DialogContent>
      </div>
    </Popover>
  )
}
